import { categories, vocabulary, countByCategory } from '../data/vocabulary.js';
import { stats } from '../utils/srs.js';
import { AudioButton } from '../components/AudioButton.js';

const HIGHLIGHT_IDS = ['hei', 'takk', 'unnskyld', 'jeg', 'aha', 'snakkerduengelsk'];

export function Dashboard(root) {
  const s = stats(vocabulary);
  const pctMastered = s.total ? Math.round((s.mastered / s.total) * 100) : 0;
  const pctSeen = s.total ? Math.round((s.seen / s.total) * 100) : 0;
  const highlights = vocabulary.filter(v => HIGHLIGHT_IDS.includes(v.id));

  root.innerHTML = `
    <header class="mb-10">
      <div class="text-[11px] uppercase tracking-[0.18em] text-moss-700 font-semibold">
        The Survival 100
      </div>
      <h1 class="font-display text-4xl sm:text-5xl font-semibold text-slate2-900
                 mt-2 tracking-tight">
        Velkommen.
      </h1>
      <p class="mt-3 text-slate2-500 max-w-2xl leading-relaxed">
        Start with the highest-frequency words and phrases in Norwegian.
        Every entry is spoken at a slightly slowed pace so your ear can catch
        the unique vowel sounds and pitch accent.
      </p>
    </header>

    <section class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
      ${MetricCard({
        label: 'Mastered',
        big: s.mastered,
        suffix: `/ ${s.total} words`,
        bar: pctMastered,
        accent: 'moss',
      })}
      ${MetricCard({
        label: 'Learning',
        big: s.learning,
        suffix: 'in rotation',
        bar: pctSeen,
        accent: 'sand',
        note: 'Words you have seen but not locked in yet.',
      })}
      ${MetricCard({
        label: 'Fresh',
        big: s.fresh,
        suffix: 'still to discover',
        bar: 100 - pctSeen,
        accent: 'sand',
        cta: { href: '/flashcards', label: 'Start a session →' },
      })}
    </section>

    <section class="mb-12">
      <div class="flex items-end justify-between mb-5">
        <h2 class="font-display text-2xl font-semibold text-slate2-900">
          Snack-sized modules
        </h2>
        <a href="/vocab" data-link
           class="text-sm text-moss-700 hover:text-moss-800 font-medium">
          All vocabulary →
        </a>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        ${categories.map(CategoryCard).join('')}
      </div>
    </section>

    <section class="mb-12">
      <h2 class="font-display text-2xl font-semibold text-slate2-900 mb-5">
        Hear it now
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        ${highlights.map(w => `
          <div class="card flex items-center justify-between gap-3">
            <div class="min-w-0">
              <div class="font-display text-lg font-semibold text-slate2-900 truncate">${w.no}</div>
              <div class="text-sm text-slate2-500 truncate">${w.en}</div>
            </div>
            ${AudioButton(w.no)}
          </div>
        `).join('')}
      </div>
    </section>

    <section class="card bg-gradient-to-br from-moss-100 via-sand-50 to-sand-100
                    border-moss-300/30 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
        <div>
          <h3 class="font-display text-xl font-semibold text-slate2-900">
            Try the Grammar Playground
          </h3>
          <p class="text-sm text-slate2-500 mt-1.5 max-w-xl leading-relaxed">
            Norwegian sentences follow a strict V2 rule — the verb is always the
            second element. Drag word blocks into the right order and the verb
            slot lights up green when you nail it.
          </p>
        </div>
        <a href="/grammar/building-blocks" data-link class="btn-primary">
          Open playground →
        </a>
      </div>
    </section>
  `;
}

function MetricCard({ label, big, suffix, bar, accent, note, cta }) {
  const barColor = accent === 'moss' ? 'bg-moss-700' : 'bg-sand-400';
  return `
    <div class="card">
      <div class="text-[11px] uppercase tracking-wider text-slate2-500 font-semibold">${label}</div>
      <div class="mt-2 flex items-baseline gap-1.5">
        <span class="font-display text-4xl font-semibold text-slate2-900">${big}</span>
        <span class="text-slate2-500 text-sm">${suffix}</span>
      </div>
      <div class="mt-3 h-1.5 w-full bg-sand-200 rounded-full overflow-hidden">
        <div class="h-full ${barColor} transition-all duration-700"
             style="width: ${Math.max(0, Math.min(100, bar))}%"></div>
      </div>
      ${note ? `<div class="mt-3 text-xs text-slate2-500">${note}</div>` : ''}
      ${cta ? `
        <div class="mt-3">
          <a href="${cta.href}" data-link
             class="text-sm text-moss-700 hover:text-moss-800 font-medium">${cta.label}</a>
        </div>` : ''}
    </div>
  `;
}

function CategoryCard(c) {
  const count = countByCategory(c.id);
  return `
    <a href="/vocab/${c.id}" data-link
       class="card card-interactive group block">
      <div class="flex items-start justify-between gap-3 mb-3">
        <div class="text-3xl">${c.icon}</div>
        <span class="badge bg-sand-100 text-slate2-500
                     group-hover:bg-moss-100 group-hover:text-moss-800
                     transition-colors">${count} words</span>
      </div>
      <div class="font-display text-lg font-semibold text-slate2-900">${c.title}</div>
      <div class="text-xs text-moss-700 font-medium mt-0.5 mb-2">${c.norwegian}</div>
      <p class="text-sm text-slate2-500 leading-relaxed">${c.description}</p>
      <div class="mt-4 text-xs text-slate2-500 flex items-center gap-1.5">
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        ~${c.estimatedMinutes} min
      </div>
    </a>
  `;
}
